/** Tunable constants shared across the scene. Edit here, not in component files. */
export const config = {
  renderer: {
    pixelRatioCap: 2,
    pixelRatioCapMobile: 1.5,
    toneMappingExposure: 1.0,
    backgroundColor: 0x000000,
  },
  camera: {
    fov: 35,
    /** Multiplier of the model bounding-sphere radius for camera distance. Higher = wider shot. */
    distanceMultiplier: 3.2,
    /** Multiplier for Sicbo (3 machines side-by-side). Smaller because content is wider, not taller. */
    distanceMultiplierSicbo: 1.55,
    /** How much higher than chamber center to look (× chamber radius). 0 = look at center, 1 = look at top. */
    lookAtLiftFactor: 0.55,
    near: 0.5,
    far: 2000,
  },
  lights: {
    ambient: { color: 0xffffff, intensity: 0.45 },
    key: { color: 0xffffff, intensity: 2.5, position: [4, 8, 4] as const, castShadow: true },
    fill: { color: 0xffffff, intensity: 0.9, position: [-5, 3, 2] as const },
    rim: { color: 0xffffff, intensity: 1.2, position: [0, 4, -6] as const },
  },
  floor: {
    size: 60,
    shadowOpacity: 0.28,
  },
  machine: {
    /** Horizontal spacing between Sicbo machines as a fraction of machine bounding-sphere diameter. */
    sicboSpacingFactor: 0.5,
  },
  balls: {
    /**
     * Single ball radius factor used by ALL game profiles, so a ball is the same size
     * regardless of how many balls are in the chamber. Sized to fit 48 comfortably.
     */
    radiusFactorVsChamber: 0.15,
    /** Inset from the chamber wall (fraction of chamber radius).
     *  The chamber mesh's bounding sphere is bigger than the actual visible glass dome because
     *  Three's bounding sphere includes the top opening geometry. A generous inset keeps balls
     *  clearly inside the visible glass instead of spilling into the dead zone outside it. */
    wallInset: 0.42,
    /** Texture resolution per ball (square). Lower for mobile. */
    textureResolution: 512,
    textureResolutionMobile: 256,
    /** Number of digit repeats around the equator. */
    digitRepeats: 4,
    materialRoughness: 0.32,
    materialMetalness: 0.0,
    materialClearcoat: 0.6,
    materialClearcoatRoughness: 0.18,
  },
  physics: {
    /** Per-second values (multiplied by dt). Tuned for a real "blower lottery machine" feel —
     *  moderate constant airflow + gravity stronger than air (so balls always want to fall),
     *  PLUS a concentrated jet boost in the lower chamber column that violently launches balls
     *  upward (like a real air nozzle). Strong noise for chaos; bouncy walls for kinetic energy.
     *
     *  Forces are expressed in the world units of the GLB (model is ~50 units tall, chamber R≈12),
     *  so values are larger than they would be at 1m-per-unit scale.
     */
    /** Modeled on a real blower lottery machine. Air force is slightly less than gravity so balls
     *  naturally fall, but the jet (which covers the entire lower hemisphere with a wide column)
     *  violently launches them upward. The upper hemisphere has no jet — balls there fall.
     *  Net effect: continuous fountain — balls erupt from below, peak in the upper chamber, fall
     *  back, get re-launched. Strong noise keeps the column non-laminar.
     *  Values bumped for snappier, more energetic motion. */
    airForceUp: 13.0,
    /** Slightly stronger gravity so balls have a clear "weight" — they fall, get jet-launched,
     *  fall, etc. — instead of floating. */
    gravity: 15.0,
    /** Jet acts on balls below center.y + (chamberR * factor). 0 = entire lower hemisphere. */
    jetExtraBelowFactor: 0.0,
    /** Bumped to overpower the heavier gravity — keeps the fountain energetic. */
    jetExtraStrength: 78.0,
    /** Lateral falloff radius — wide so most lower-hemisphere balls get lift. */
    jetColumnRadiusFactor: 0.85,
    noiseAmplitude: 48.0,
    /** Very low damping — keep energy in the system so balls keep bouncing all over. */
    damping: 0.35,
    repulsionStrength: 110.0,
    /** Highly elastic walls — balls bounce off the glass like in a real machine. */
    wallRestitution: 0.88,
    /** Cap absolute velocity. With innerR ≈ 6.5 and dt clamped at 1/30, 26 m/s means
     *  worst-case travel of 0.87 units/frame — still well under wall distance, no tunneling. */
    maxSpeed: 26.0,
    /** No centering force — let walls + repulsion do the work so balls fill the chamber. */
    centeringStrength: 0.0,
  },
  reveal: {
    /** Duration of the path from chamber → end of track. The baked exit animation in the GLB
     *  takes ~6.6s in the original; we compress it for snappier UX. The selector's dip-and-rise
     *  plays IN PARALLEL with this, matching the model creator's baked timing. */
    transitDurationSec: 2.4,
    /** Duration of the return-to-pool arc (start of next draw). */
    returnDurationSec: 1.4,
    /** Idle spin period while ball is displayed (seconds per full Y rotation). */
    idleSpinPeriodSec: 6.0,
    /** Selection-zone hover height factor (used only when no resultHolder exists in the model). */
    revealHoverFactor: 1.55,
    /** Mesh-scale multiplier while the ball is in the "displayed" state. Pool balls are already
     *  large enough that the result reads clearly without further scaling. */
    displayedScale: 1.0,
    displayedScaleTweenSec: 0.35,
  },
  sequencer: {
    introChurnSec: 2.0,
    sicboStaggerSec: 0.2,
    outroSec: 1.0,
  },
  selectionZone: {
    color: 0xffd34a,
    beamColor: 0xffe48a,
    ringInnerFactor: 0.45, // of chamber radius
    ringOuterFactor: 0.62,
    ringThickness: 0.12,
    beamRadiusFactor: 0.18,
    ringSpinSpeedRad: 0.5, // rad/s
  },
} as const;

export type AppConfig = typeof config;
